/**
 * HTML Confirmation Dialog for Turbo
 *
 * Replaces the browser's window.confirm for every data-turbo-confirm in the
 * application with the Bootstrap modal rendered by layouts/_confirm_dialog.
 * Turbo calls this once per confirmable action and waits on the promise: it
 * resolves true when the accept button is pressed, false for Cancel, Escape,
 * the close button or a click on the backdrop.
 *
 * Views need change nothing. A call site that wants a stronger dialog can add
 * data-confirm-title, data-confirm-subject and data-confirm-detail to the same
 * element that carries data-turbo-confirm; anything it omits is left out.
 */
import * as Turbo from '@hotwired/turbo'
import * as bootstrap from 'bootstrap'

const DIALOG_ID = 'turbo-confirm-dialog'

const DESTRUCTIVE_TRIGGER_CLASSES = ['btn-danger', 'btn-outline-danger']

/**
 * The element the person actually clicked.
 *
 * Turbo hands the confirm hook a form, and for a data-turbo-method link that form is one
 * it built itself moments earlier, copying across exactly five attributes: method,
 * data-turbo-frame, data-turbo-action, data-turbo-confirm and data-turbo-stream. A
 * data-confirm-subject on the link is not among them and never arrives, so the link has
 * to be caught on the way past.
 *
 * Capture phase, because Turbo's own click handling runs on the way back up. The value is
 * only ever used when its data-turbo-confirm matches the message Turbo passed, so a
 * leftover from an earlier click cannot be mistaken for this one.
 */
let lastClickedTrigger = null

document.addEventListener(
  'click',
  (event) => {
    lastClickedTrigger = event.target?.closest?.('[data-turbo-confirm]') ?? null
  },
  true
)

// Fallback to window.bootstrap if the module import fails to resolve correctly,
// matching how the Stimulus controllers in this application resolve Bootstrap.
const resolveBootstrapLibrary = () => (bootstrap.Modal ? bootstrap : window.bootstrap)

const el = (dialog, suffix) => dialog.querySelector(`#${DIALOG_ID}-${suffix}`)

/**
 * The HTTP verb behind this submission.
 *
 * Turbo rewrites a data-turbo-method link into a hidden form carrying the verb in its
 * method attribute, while button_to and form_with send a POST with the real verb in
 * Rails' _method override, so the override has to win.
 */
const methodOf = (formElement, submitter) => {
  const override = formElement?.querySelector('input[name="_method"]')?.value
  if (override) return override.toLowerCase()

  const method = submitter?.getAttribute('formmethod') || formElement?.getAttribute('method')
  return method ? method.toLowerCase() : 'get'
}

// A delete is destructive, and so is a button the view already styled as dangerous
// (an archive, a cache clear) even though its verb is a plain POST.
const isDestructive = (formElement, submitter, trigger) => {
  if (methodOf(formElement, submitter) === 'delete') return true

  return [submitter, trigger].some((node) =>
    DESTRUCTIVE_TRIGGER_CLASSES.some((name) => node?.classList?.contains(name))
  )
}

// Only trust the stashed link if it is the one this confirmation is about.
const triggerFor = (message, submitter) => {
  if (submitter?.dataset?.confirmSubject || submitter?.dataset?.confirmTitle) return submitter

  return lastClickedTrigger?.getAttribute('data-turbo-confirm') === message ? lastClickedTrigger : null
}

// textContent, never innerHTML - every one of these is a translated string that
// interpolates user supplied names and email addresses.
const fill = (node, value) => {
  node.textContent = value ?? ''
  node.classList.toggle('d-none', !value)
}

const setBusy = (dialog, busy) => {
  el(dialog, 'spinner').classList.toggle('d-none', !busy)
  el(dialog, 'accept').disabled = busy
  el(dialog, 'cancel').disabled = busy
  el(dialog, 'close').disabled = busy
}

const confirmWithDialog = (message, formElement, submitter) => {
  const dialog = document.getElementById(DIALOG_ID)
  const bs = resolveBootstrapLibrary()

  // Layouts without the partial (print, error pages) keep the browser dialog
  // rather than losing the confirmation step altogether.
  if (!dialog || !bs?.Modal) return Promise.resolve(window.confirm(message))

  const trigger = triggerFor(message, submitter)
  const destructive = isDestructive(formElement, submitter, trigger)
  const acceptButton = el(dialog, 'accept')

  fill(el(dialog, 'message'), message)
  fill(el(dialog, 'subject'), trigger?.dataset?.confirmSubject)
  fill(el(dialog, 'detail'), trigger?.dataset?.confirmDetail)
  el(dialog, 'identity').classList.toggle('d-none', !trigger?.dataset?.confirmSubject)
  el(dialog, 'title').textContent =
    trigger?.dataset?.confirmTitle || (destructive ? dialog.dataset.deleteLabel : dialog.dataset.confirmLabel)

  el(dialog, 'accept-label').textContent = destructive ? dialog.dataset.deleteLabel : dialog.dataset.confirmLabel
  acceptButton.classList.toggle('btn-danger', destructive)
  acceptButton.classList.toggle('btn-primary', !destructive)
  setBusy(dialog, false)

  const modal = bs.Modal.getOrCreateInstance(dialog)

  return new Promise((resolve) => {
    let settled = false
    let busy = false

    const settle = (answer) => {
      if (settled) return
      settled = true
      resolve(answer)
    }

    const stopListening = () => {
      acceptButton.removeEventListener('click', accept)
      dialog.removeEventListener('hide.bs.modal', blockHideWhileBusy)
      formElement.removeEventListener('turbo:submit-end', onSubmitEnd)
      document.removeEventListener('turbo:before-render', onBeforeRender)
    }

    /**
     * Accepting does NOT close the dialog.
     *
     * Turbo only submits once this promise resolves, and the round trip that follows is
     * long enough to press Delete a second time. Closing first handed the page back
     * looking untouched - the row still listed, the button still live - so the only
     * signal anything had happened was the row eventually vanishing. The dialog stays up
     * in a working state instead, which both reports progress and puts a backdrop over
     * everything that must not be clicked again. onSubmitEnd takes it down once the
     * response is in.
     */
    function accept() {
      busy = true
      setBusy(dialog, true)
      settle(true)
    }

    // Escape, a backdrop click and the close button all route through hide.bs.modal, and
    // Bootstrap honours preventDefault on it. Disabling the buttons alone would leave two
    // of those three still able to hand the page back while the delete is in flight.
    function blockHideWhileBusy(event) {
      if (busy) event.preventDefault()
    }

    // Cancel, Escape, the close button, or a click on the backdrop.
    function onHidden() {
      stopListening()
      modal.dispose()
      settle(false)
    }

    /**
     * The response has arrived and Turbo is about to render it, so the dialog's work is
     * done either way.
     *
     * This used to return early on success and wait for the body to be replaced. That only
     * happens on a full page visit. Most of the confirmable actions in this application sit
     * inside <turbo-frame id="profile-frame">, and a submission scoped to a frame swaps
     * that frame and leaves the body - and therefore this dialog - exactly where it was.
     * The spinner then ran forever.
     *
     * Bound to the form rather than to document, so no other submission can be mistaken for
     * this one. Turbo redirects the event to documentElement when the form has already been
     * detached, which happens only on a full page visit - and there the body, this dialog
     * included, is being replaced anyway, so onBeforeRender is what tidies up.
     */
    function onSubmitEnd() {
      stopListening()
      busy = false
      setBusy(dialog, false)
      dialog.removeEventListener('hidden.bs.modal', onHidden)
      dialog.addEventListener('hidden.bs.modal', () => modal.dispose(), { once: true })
      modal.hide()
    }

    // Backstop for a full page visit, where the dialog leaves with the body it sits in and
    // no hidden.bs.modal ever fires. Without it Bootstrap keeps hold of a detached element.
    function onBeforeRender() {
      stopListening()
      dialog.removeEventListener('hidden.bs.modal', onHidden)
      modal.dispose()
    }

    acceptButton.addEventListener('click', accept, { once: true })
    dialog.addEventListener('hide.bs.modal', blockHideWhileBusy)
    dialog.addEventListener('hidden.bs.modal', onHidden, { once: true })
    formElement.addEventListener('turbo:submit-end', onSubmitEnd, { once: true })
    document.addEventListener('turbo:before-render', onBeforeRender, { once: true })

    modal.show()
  })
}

Turbo.config.forms.confirm = confirmWithDialog
